// frontend/admin-portal/src/pages/GoodsReceipts.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Table,
  Badge,
  Alert,
  Input,
} from "@fiberhome/shared-ui";
import { goodsReceiptService } from "../services/goodsReceiptService";
import ReceiptDetailModal from "../components/procurement/ReceiptDetailModal";

export default function GoodsReceipts() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ page: 1, limit: 20, search: "" });
  const [receiptModalId, setReceiptModalId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== "" && v != null) params[k] = v;
      }
      const { items, pagination } = await goodsReceiptService.list(params);
      setItems(items);
      setPagination(pagination);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <PageHeader
        title="Goods Receipts"
        subtitle="Every stock receipt recorded against a PO"
        breadcrumbs={["Home", "Procurement", "Goods Receipts"]}
      />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Input
          placeholder="Search by receipt number..."
          value={filters.search}
          onChange={(e) =>
            setFilters({ ...filters, search: e.target.value, page: 1 })
          }
        />
      </Card>

      <Card
        title={`Receipts (${pagination.total})`}
        actions={
          <div className="fh-products-toolbar__pager">
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            >
              Prev
            </Button>
            <span className="text-muted" style={{ fontSize: 12 }}>
              Page {pagination.page} / {pagination.pages || 1}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            >
              Next
            </Button>
          </div>
        }
      >
        <Table
          columns={[
            {
              key: "code",
              label: "Receipt #",
              render: (r) => <strong>{r.code}</strong>,
            },
            {
              key: "receivedAt",
              label: "Received",
              render: (r) => new Date(r.receivedAt).toLocaleString(),
            },
            {
              key: "supplier",
              label: "Supplier",
              render: (r) => r.supplier?.name || "—",
            },
            {
              key: "purchaseOrder",
              label: "PO",
              render: (r) =>
                r.purchaseOrder ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      navigate(`/purchase-orders/${r.purchaseOrder.id}`)
                    }
                  >
                    {r.purchaseOrder.code}
                  </Button>
                ) : (
                  "—"
                ),
            },
            { key: "lineCount", label: "Lines" },
            {
              key: "status",
              label: "Status",
              render: (r) => (
                <Badge variant={r.status === "posted" ? "success" : "default"}>
                  {r.status}
                </Badge>
              ),
            },
            {
              key: "actions",
              label: "",
              width: 100,
              render: (r) => (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setReceiptModalId(r.id)}
                >
                  View
                </Button>
              ),
            },
          ]}
          data={items}
          loading={loading}
          emptyMessage="No goods receipts yet"
        />
      </Card>

      {receiptModalId && (
        <ReceiptDetailModal
          open={!!receiptModalId}
          receiptId={receiptModalId}
          onClose={() => setReceiptModalId(null)}
          onCancelled={load}
        />
      )}
    </>
  );
}
